import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileSignature, PlusCircle, Search, Calendar, Eye, Copy, Loader2 } from 'lucide-react';
import { vendorContractApi } from '../../services/vendorContractService';

const statusColor = (status) => {
  const map = {
    draft: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-800',
    viewed: 'bg-amber-100 text-amber-800',
    signed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-700',
    expired: 'bg-orange-100 text-orange-800',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const VendorContracts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-contracts', searchTerm, status],
    queryFn: () =>
      vendorContractApi.list({
        search: searchTerm || undefined,
        status: status || undefined,
        per_page: 30,
      }),
  });

  const contracts = data?.data || [];

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Contracts</h1>
            <p className="text-sm text-gray-600">
              Create contracts from templates, draft with AI, and collect online signatures.
            </p>
          </div>
          <Link
            to="/vendor/contracts/create"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md shadow-sm bg-primary-600 text-white hover:bg-primary-700"
          >
            <PlusCircle className="w-4 h-4" />
            New contract
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search contracts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm w-full"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="signed">Signed</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            Failed to load contracts.
          </div>
        ) : contracts.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
            <FileSignature className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-medium mb-1">No contracts yet</p>
            <p className="text-sm text-gray-500 mb-4">Start from a template or draft with AI.</p>
            <Link
              to="/vendor/contracts/create"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:underline"
            >
              <PlusCircle className="w-4 h-4" />
              Create your first contract
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {contracts.map((c) => (
              <div key={c.id} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <FileSignature className="w-4 h-4 text-gray-400 shrink-0" />
                      <p className="font-semibold text-gray-900 truncate">{c.title}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor(c.status)}`}>
                        {c.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {c.contract_number} · {c.client_name}
                      {c.client_email ? ` · ${c.client_email}` : ''}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <Calendar className="w-3.5 h-3.5" />
                      {c.created_at
                        ? new Date(c.created_at).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        : '—'}
                      {c.signed_at && (
                        <span className="text-green-700">
                          · Signed {new Date(c.signed_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <Link
                      to={`/vendor/contracts/create?from=${c.id}`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                      title="Copy for another client"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </Link>
                    <Link
                      to={`/vendor/contracts/${c.id}`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      <Eye className="w-4 h-4" />
                      Open
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorContracts;
